<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useContentStore } from '../stores/contentStore';
import { useRoute, useRouter } from 'vue-router';
import Icon from '../components/Common/Icon.vue';
import Image from '../components/Common/Image.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Controller } from 'swiper/modules';
import { gsap } from 'gsap';
import Video from '../components/Common/Video.vue';
import { useLocalizationStore } from '../stores/localizationStore';

const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const story = ref(contentStore.content.categories[route.params.categoryId].stories[route.params.storyId]);

const { $localize } = useLocalizationStore();

const sliderContainer = useTemplateRef('slider-container');
onMounted(() => {
    gsap.from(sliderContainer.value, {
        opacity: 0,
        x: -100,
        duration: 0.6,
        delay: 0.5,
        ease: "power2.out",
    })
})

const contentSwiper = ref(null);
const mediaSwiper = ref(null);

const hasNext = ref(false);
const hasPrev = ref(false);

function onContentSwiper(swiper){
    contentSwiper.value = swiper;
    updateNavigationState();
}

function onMediaSwiper(swiper){
    mediaSwiper.value = swiper;
}

function onNext(){
    contentSwiper.value.slideNext();
}

function onPrev(){
    contentSwiper.value.slidePrev();
}

function onSlideChange(){
    if(contentSwiper.value?.realIndex === Object.keys(story.value.slides).length){
        router.push({name: 'result', params: route.params})
        return;
    }

    updateNavigationState();
}

function updateNavigationState(){
    hasPrev.value = contentSwiper.value?.realIndex > 0;
    hasNext.value = contentSwiper.value?.realIndex < Object.keys(story.value.slides).length;
}
</script>

<template>
    <div class="grow grid grid-cols-2 xl:grid-cols-5 gap-x-24 xl:gap-x-48 pt-52 px-10 4k:px-16 4k:pt-72">
        <div class="xl:col-span-2">
            <div ref="slider-container" class="rounded-t-lg bg-white/20 backdrop-blur-2xl h-full p-16 flex flex-col">
                <Swiper
                    class="grow w-full overflow-hidden"
                    :modules="[Navigation, Controller]"
                    :controller="{control: mediaSwiper}"
                    :slides-per-view="1"
                    :space-between="20"
                    @swiper="onContentSwiper"
                    @slideChange="onSlideChange"
                >
                    <SwiperSlide
                        class="!flex flex-col pt-2"
                        v-for="(slide, i) in story.slides"
                        :key="i"
                    >
                        <h2 class="r-text-43 font-medium mb-3 xl:mb-5" v-text="$localize(slide.left.title)"></h2>
                        <div class="grow relative overflow-y-auto mb-5 no-scrollbar">
                            <div class="wysiwyg absolute left-0 top-0 size-full" v-html="$localize(slide.left.content)"></div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide />
                </Swiper>

                <div class="flex gap-x-12 xl:gap-x-16">
                    <button
                        @click="onPrev()"
                        type="button"
                        class="shrink-0 bg-white text-gray-800 size-16 xl:size-20 rounded-full grid place-items-center transition"
                        :class="hasPrev ? 'hover:scale-95' : 'opacity-50 pointer-events-none'"
                    >
                        <Icon class="size-5 xl:size-7" name="arrow-left" />
                    </button>
                    <button
                        @click="onNext()"
                        type="button"
                        class="shrink-0 bg-white text-gray-800 size-16 xl:size-20 rounded-full grid place-items-center transition"
                        :class="hasNext ? 'hover:scale-95' : 'opacity-50 pointer-events-none'"
                    >
                        <Icon class="size-5 xl:size-7" name="arrow-right" />
                    </button>
                </div>
            </div>
        </div>

        <div class="xl:col-span-3">
            <Swiper
                class="w-full overflow-hidden mx-auto max-w-md xl:max-w-lg 4k:max-w-3xl"
                :modules="[Controller]"
                :controller="{control: contentSwiper}"
                :slides-per-view="1"
                :space-between="20"
                @swiper="onMediaSwiper"
            >
                <SwiperSlide
                    class="space-y-10"
                    v-for="(slide, i) in story.slides"
                    :key="i"
                    :class="slide.right.media.length === 1 ? '!h-auto !flex flex-col justify-center' : ''"
                >
                    <template
                        v-for="(media, m) in slide.right.media"
                        :key="m"
                    >
                        <Image
                            v-if="media.type === 'image'"
                            class="aspect-video"
                            :auto-aspect-ratio="false"
                            :light-box="true"
                            :image="media"
                        />
                        <Video
                            v-if="media.type === 'video'"
                            class="aspect-video"
                            :video="media"
                        />
                    </template>
                </SwiperSlide>
                <SwiperSlide />
            </Swiper>
        </div>
    </div>
</template>
